import { useEffect } from 'react';

import { Button, Group, Modal, Stack, TextInput } from '@mantine/core';
import { useForm } from '@mantine/form';

interface DuplicateReportConfigModalProps {
  opened: boolean;
  onClose: () => void;
  onConfirm: (newName: string, newSlug: string) => void;
  originalName: string;
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .replaceAll(/[^a-z0-9]+/g, '-')
    .replaceAll(/^-|-$/g, '');
}

export function DuplicateReportConfigModal({
  opened,
  onClose,
  onConfirm,
  originalName,
}: Readonly<DuplicateReportConfigModalProps>) {
  const form = useForm({
    mode: 'controlled',
    initialValues: {
      name: '',
      slug: '',
    },
    validate: {
      name: (value) => (!value.trim() ? 'Name is required' : null),
      slug: (value) => (!value.trim() ? 'Slug is required' : null),
    },
  });

  useEffect(() => {
    if (opened) {
      const newName = `${originalName} (Copy)`;
      form.setValues({
        name: newName,
        slug: slugify(newName),
      });
    }
  }, [opened, originalName]);

  const handleSubmit = (values: { name: string; slug: string }) => {
    onConfirm(values.name, values.slug);
    form.reset();
  };

  return (
    <Modal opened={opened} onClose={onClose} title="Duplicate Configuration" size="sm">
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack>
          <TextInput
            withAsterisk
            label="New Name"
            placeholder="Enter a name for the copy"
            {...form.getInputProps('name')}
            onChange={(e) => {
              form.setFieldValue('name', e.currentTarget.value);
              form.setFieldValue('slug', slugify(e.currentTarget.value));
            }}
          />
          <TextInput
            withAsterisk
            label="New Slug"
            placeholder="url-safe-identifier"
            {...form.getInputProps('slug')}
          />
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit">Duplicate</Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
